export interface CustomCaseSuspect {
  fullName: string;
  age: string;
  eyes: string;
  height: string;
  occupation: string;
  relationship: string;
  hairColor: string;
  hairLength: string;
  sex: "M" | "F" | "N/A" | "";
  skinTone: string;
  notes: string;
}

export interface CustomCaseFormData {
  firstName: string;
  email: string;
  city: string;
  location: string;
  suspects: CustomCaseSuspect[];
  source: string;
  marketingOptIn: boolean;
  policyAccepted: boolean;
}

export interface CustomCaseSuspectError {
  index: number;
  field: keyof CustomCaseSuspect | "general";
  message: string;
}

export interface CustomCaseFormErrors {
  firstName?: string;
  email?: string;
  city?: string;
  location?: string;
  source?: string;
  suspects?: CustomCaseSuspectError[];
}

export const CUSTOM_CASE_FORM_STORAGE_KEY = "custom-story-form-data";
export const CUSTOM_CASE_LAST_ORDER_TOKEN_KEY = "custom-case-last-order-token";
export const CUSTOM_CASE_LAST_ORDER_EMAIL_KEY = "custom-case-last-order-email";
export const CUSTOM_CASE_PENDING_ORDER_TOKEN_KEY = "custom-case-pending-order-token";
export const CUSTOM_CASE_PRODUCT_PATH =
  import.meta.env.VITE_CUSTOM_CASE_PRODUCT_PATH || "custom-cold-case";
export const CUSTOM_CASE_CHECKOUT_URL = import.meta.env.VITE_CUSTOM_CASE_CHECKOUT_URL || "";
export const CUSTOM_CASE_COMING_SOON_ENABLED =
  String(import.meta.env.VITE_CUSTOM_CASE_COMING_SOON_ENABLED || "").trim().toLowerCase() === "true";
export const CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT = String(
  import.meta.env.VITE_CUSTOM_CASE_COMING_SOON_FORMSPREE_ENDPOINT || ""
).trim();
export const MIN_CUSTOM_CASE_SUSPECTS = 1;
export const TARGET_CUSTOM_CASE_SUSPECTS = 4;
export const MAX_CUSTOM_CASE_SUSPECTS = 6;
export const CUSTOM_CASE_FIELD_LIMITS = Object.freeze({
  firstName: 30,
  city: 80,
  location: 120,
  suspectFullName: 30,
  age: 20,
  occupation: 80,
  relationship: 100,
  notes: 240,
});

const normalizeString = (value = "") => String(value || "").trim();
const normalizeWhitespace = (value = "") => normalizeString(value).replace(/\s+/g, " ");
const NAME_TOKEN_PATTERN = /^[\p{L}][\p{L}'-]*$/u;
const DESCRIPTIVE_TEXT_PATTERN = /^[\p{L}\p{N} .,'&()/:+\-]*$/u;
const PROMPT_INJECTION_PATTERNS = [
  /ignore\s+(all\s+)?(previous|prior|above)\s+instructions/i,
  /\b(system prompt|developer message|hidden prompt)\b/i,
  /\b(reveal|repeat|show)\s+(the\s+)?(system|developer|hidden)?\s*prompt\b/i,
  /\byou are chatgpt\b/i,
  /\b(output|return)\s+exactly\b/i,
];
const PROMPT_MARKUP_PATTERN = /```|<\s*\/?\s*(system|assistant|developer|tool|user)\b/i;
const URL_PATTERN = /\b(?:https?:\/\/|www\.)\S+/i;
const REQUIRED_SUSPECT_FIELDS: Array<keyof CustomCaseSuspect> = [
  "fullName",
  "age",
  "sex",
  "hairColor",
  "skinTone",
  "occupation",
];

export const getEmptySuspect = (): CustomCaseSuspect => ({
  fullName: "",
  age: "",
  eyes: "",
  height: "",
  occupation: "",
  relationship: "",
  hairColor: "",
  hairLength: "",
  sex: "",
  skinTone: "",
  notes: "",
});

export const getDefaultCustomCaseFormData = (): CustomCaseFormData => ({
  firstName: "",
  email: "",
  city: "",
  location: "",
  suspects: [],
  source: "",
  marketingOptIn: false,
  policyAccepted: false,
});

export const loadStoredCustomCaseFormData = (): CustomCaseFormData => {
  if (typeof window === "undefined") return getDefaultCustomCaseFormData();
  const saved = window.sessionStorage.getItem(CUSTOM_CASE_FORM_STORAGE_KEY);
  if (!saved) return getDefaultCustomCaseFormData();

  try {
    const parsed = JSON.parse(saved);
    return {
      ...getDefaultCustomCaseFormData(),
      ...parsed,
      suspects: Array.isArray(parsed?.suspects)
        ? parsed.suspects.map((suspect: Partial<CustomCaseSuspect>) => ({
            ...getEmptySuspect(),
            ...suspect,
          }))
        : [],
    };
  } catch {
    return getDefaultCustomCaseFormData();
  }
};

export const storeCustomCaseFormData = (formData: CustomCaseFormData) => {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(CUSTOM_CASE_FORM_STORAGE_KEY, JSON.stringify(formData));
};

export const clearStoredCustomCaseFormData = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(CUSTOM_CASE_FORM_STORAGE_KEY);
};

export const saveLastCustomOrderAccessToken = (accessToken: string) => {
  if (typeof window === "undefined") return;
  const normalized = String(accessToken || "").trim();
  if (!normalized) return;
  window.localStorage.setItem(CUSTOM_CASE_LAST_ORDER_TOKEN_KEY, normalized);
};

export const getLastCustomOrderAccessToken = () => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(CUSTOM_CASE_LAST_ORDER_TOKEN_KEY) || "").trim();
};

export const clearLastCustomOrderAccessToken = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(CUSTOM_CASE_LAST_ORDER_TOKEN_KEY);
};

export const savePendingCustomOrderAccessToken = (accessToken: string) => {
  if (typeof window === "undefined") return;
  const normalized = String(accessToken || "").trim();
  if (!normalized) return;
  window.sessionStorage.setItem(CUSTOM_CASE_PENDING_ORDER_TOKEN_KEY, normalized);
};

export const getPendingCustomOrderAccessToken = () => {
  if (typeof window === "undefined") return "";
  return String(window.sessionStorage.getItem(CUSTOM_CASE_PENDING_ORDER_TOKEN_KEY) || "").trim();
};

export const clearPendingCustomOrderAccessToken = () => {
  if (typeof window === "undefined") return;
  window.sessionStorage.removeItem(CUSTOM_CASE_PENDING_ORDER_TOKEN_KEY);
};

export const saveLastCustomOrderEmail = (email: string) => {
  if (typeof window === "undefined") return;
  const normalized = String(email || "").trim().toLowerCase();
  if (!normalized) return;
  window.localStorage.setItem(CUSTOM_CASE_LAST_ORDER_EMAIL_KEY, normalized);
};

export const getLastCustomOrderEmail = () => {
  if (typeof window === "undefined") return "";
  return String(window.localStorage.getItem(CUSTOM_CASE_LAST_ORDER_EMAIL_KEY) || "")
    .trim()
    .toLowerCase();
};

export const clearLastCustomOrderEmail = () => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(CUSTOM_CASE_LAST_ORDER_EMAIL_KEY);
};

export const extractCustomCaseFirstName = (value = "") =>
  normalizeString(value).split(/\s+/).filter(Boolean)[0] || "";

export const isSingleTokenName = (value = "") =>
  extractCustomCaseFirstName(value) === normalizeString(value) && Boolean(normalizeString(value));

export const getIncompleteSuspectFields = (suspect: CustomCaseSuspect): Array<keyof CustomCaseSuspect> =>
  REQUIRED_SUSPECT_FIELDS.filter((field) => {
    if (field === "fullName") {
      return !isSingleTokenName(suspect?.fullName);
    }
    return !normalizeString(suspect?.[field]);
  });

export const isCustomCaseSuspectComplete = (suspect: CustomCaseSuspect) =>
  getIncompleteSuspectFields(suspect).length === 0;

export const formatSuspectDetails = (suspect: CustomCaseSuspect, index: number) => {
  const parts = [`Suspect ${index + 1}: ${suspect.fullName}`];
  if (suspect.relationship) parts.push(`relationship: ${suspect.relationship}`);
  if (suspect.sex) parts.push(`sex: ${suspect.sex}`);
  if (suspect.age) parts.push(`age: ${suspect.age}`);
  if (suspect.eyes) parts.push(`eyes: ${suspect.eyes}`);
  if (suspect.hairColor) parts.push(`hair: ${suspect.hairColor}`);
  if (suspect.hairLength) parts.push(`hair length: ${suspect.hairLength}`);
  if (suspect.height) parts.push(`height: ${suspect.height}`);
  if (suspect.occupation) parts.push(`occupation: ${suspect.occupation}`);
  if (suspect.skinTone) parts.push(`skin tone: ${suspect.skinTone}`);
  if (suspect.notes) parts.push(`notes: ${suspect.notes}`);
  return parts.join(", ");
};

export const validateCustomCaseForm = (formData: CustomCaseFormData): CustomCaseFormErrors => {
  const errors: CustomCaseFormErrors = {};
  const suspectErrors: CustomCaseSuspectError[] = [];
  const hasPromptInjectionMarkers = (value = "") =>
    PROMPT_INJECTION_PATTERNS.some((pattern) => pattern.test(String(value || "")));
  const hasPromptMarkupMarkers = (value = "") =>
    PROMPT_MARKUP_PATTERN.test(String(value || "")) || URL_PATTERN.test(String(value || ""));
  const validatePlainTextField = (
    value: string,
    maxLength: number,
    label: string,
    options: { allowEmpty?: boolean; allowPattern?: RegExp | null } = {}
  ) => {
    const normalized = normalizeWhitespace(value);
    if (!normalized) {
      return options.allowEmpty ? "" : `${label} is required`;
    }
    if (normalized.length > maxLength) {
      return `${label} must be ${maxLength} characters or fewer`;
    }
    if (hasPromptInjectionMarkers(normalized) || hasPromptMarkupMarkers(normalized)) {
      return `${label} must only contain real descriptive details`;
    }
    if (options.allowPattern && !options.allowPattern.test(normalized)) {
      return `${label} contains unsupported characters`;
    }
    return "";
  };

  if (!formData.firstName) {
    errors.firstName = "Your first name is required";
  } else {
    const normalizedFirstName = extractCustomCaseFirstName(formData.firstName);
    if (normalizedFirstName.length > CUSTOM_CASE_FIELD_LIMITS.firstName) {
      errors.firstName = `First name must be ${CUSTOM_CASE_FIELD_LIMITS.firstName} characters or fewer`;
    } else if (!NAME_TOKEN_PATTERN.test(normalizedFirstName)) {
      errors.firstName = "First name may only include letters, apostrophes, or hyphens";
    }
  }

  if (!formData.email) {
    errors.email = "Email is required";
  } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
    errors.email = "Please enter a valid email";
  }

  if (!formData.city) {
    errors.city = "City is required";
  } else {
    const cityError = validatePlainTextField(
      formData.city,
      CUSTOM_CASE_FIELD_LIMITS.city,
      "City",
      { allowPattern: DESCRIPTIVE_TEXT_PATTERN }
    );
    if (cityError) errors.city = cityError;
  }

  if (normalizeString(formData.location)) {
    const locationError = validatePlainTextField(
      formData.location,
      CUSTOM_CASE_FIELD_LIMITS.location,
      "Location",
      { allowEmpty: true, allowPattern: DESCRIPTIVE_TEXT_PATTERN }
    );
    if (locationError) {
      errors.location = locationError;
    }
  }

  if (!formData.source) {
    errors.source = "Please tell us where you heard about us";
  }

  if (!Array.isArray(formData.suspects) || formData.suspects.length < MIN_CUSTOM_CASE_SUSPECTS) {
    errors.suspects = [
      {
        index: 0,
        field: "general",
        message: `Please add at least ${MIN_CUSTOM_CASE_SUSPECTS} suspects`,
      },
    ];
    return errors;
  }

  formData.suspects.forEach((suspect, index) => {
    if (!suspect.fullName) {
      suspectErrors.push({ index, field: "fullName", message: "First name is required" });
    } else if (!isSingleTokenName(suspect.fullName)) {
      suspectErrors.push({
        index,
        field: "fullName",
        message: "Suspects should use first names only for custom cases",
      });
    } else if (suspect.fullName.length > CUSTOM_CASE_FIELD_LIMITS.suspectFullName) {
      suspectErrors.push({
        index,
        field: "fullName",
        message: `First name must be ${CUSTOM_CASE_FIELD_LIMITS.suspectFullName} characters or fewer`,
      });
    } else if (!NAME_TOKEN_PATTERN.test(normalizeWhitespace(suspect.fullName))) {
      suspectErrors.push({
        index,
        field: "fullName",
        message: "First name may only include letters, apostrophes, or hyphens",
      });
    }
    if (!suspect.age) {
      suspectErrors.push({ index, field: "age", message: "Age is required" });
    } else {
      const ageError = validatePlainTextField(suspect.age, CUSTOM_CASE_FIELD_LIMITS.age, "Age", {
        allowPattern: DESCRIPTIVE_TEXT_PATTERN,
      });
      if (ageError) suspectErrors.push({ index, field: "age", message: ageError });
    }
    if (!suspect.sex) {
      suspectErrors.push({ index, field: "sex", message: "Sex is required" });
    }
    if (!suspect.hairColor) {
      suspectErrors.push({ index, field: "hairColor", message: "Hair color is required" });
    }
    if (!suspect.skinTone) {
      suspectErrors.push({ index, field: "skinTone", message: "Skin tone is required" });
    }
    if (!suspect.occupation) {
      suspectErrors.push({ index, field: "occupation", message: "Occupation is required" });
    } else {
      const occupationError = validatePlainTextField(
        suspect.occupation,
        CUSTOM_CASE_FIELD_LIMITS.occupation,
        "Occupation",
        { allowPattern: DESCRIPTIVE_TEXT_PATTERN }
      );
      if (occupationError) suspectErrors.push({ index, field: "occupation", message: occupationError });
    }
    if (normalizeString(suspect.relationship)) {
      const relationshipError = validatePlainTextField(
        suspect.relationship,
        CUSTOM_CASE_FIELD_LIMITS.relationship,
        "Relationship",
        { allowEmpty: true, allowPattern: DESCRIPTIVE_TEXT_PATTERN }
      );
      if (relationshipError) suspectErrors.push({ index, field: "relationship", message: relationshipError });
    }
    if (normalizeString(suspect.notes)) {
      const notesError = validatePlainTextField(suspect.notes, CUSTOM_CASE_FIELD_LIMITS.notes, "Extra notes", {
        allowEmpty: true,
        allowPattern: null,
      });
      if (notesError) suspectErrors.push({ index, field: "notes", message: notesError });
    }
  });

  if (suspectErrors.length > 0) {
    errors.suspects = suspectErrors;
  }

  return errors;
};
